import { type FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usernameSchema, type UpdateProfileDetailsRequest } from "@hybrid-escrow/shared";
import { useAuthPrincipal } from "../hooks/useAuthPrincipal";
import { apiClient, ApiClientError } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { LoadingState, describeError } from "../components/States";
import { AccountSettings } from "../components/AccountSettings";
import { usePageMeta } from "../lib/seo";
import { detectLocationLabel, GeolocationError } from "../lib/geolocation";

export function ProfilePage() {
  usePageMeta({
    title: "Your profile",
    description: "Manage your public display name on MarketOnBase.",
    path: "/profile",
    index: false,
  });

  const { isInitialized, isSignedIn, principal, email, isLoadingPrincipal } = useAuthPrincipal();
  const queryClient = useQueryClient();
  const [usernameInput, setUsernameInput] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: (username: string) => apiClient.updateProfile({ username }),
    onSuccess: (updated) => {
      // Seed the cache with the server's own response rather than just invalidating, so the
      // header/nav reflect the new name immediately instead of flickering through a refetch.
      queryClient.setQueryData(queryKeys.me, updated);
      setSaved(true);
    },
  });

  const { data: details, isLoading: detailsLoading } = useQuery({
    queryKey: queryKeys.profileDetails,
    queryFn: () => apiClient.getProfileDetails(),
    enabled: isSignedIn,
  });

  const [phone, setPhone] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [locationLabel, setLocationLabel] = useState("");
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [detailsSaved, setDetailsSaved] = useState(false);

  useEffect(() => {
    if (!details) return;
    setPhone(details.phone ?? "");
    setAddressLine1(details.addressLine1 ?? "");
    setAddressLine2(details.addressLine2 ?? "");
    setCity(details.city ?? "");
    setRegion(details.region ?? "");
    setPostalCode(details.postalCode ?? "");
    setCountry(details.country ?? "");
    setLocationLabel(details.locationLabel ?? "");
  }, [details]);

  const detailsMutation = useMutation({
    mutationFn: (body: UpdateProfileDetailsRequest) => apiClient.updateProfileDetails(body),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.profileDetails, updated);
      setDetailsSaved(true);
    },
  });

  async function handleUseLocation() {
    setLocationError(null);
    setIsDetectingLocation(true);
    try {
      const label = await detectLocationLabel();
      setLocationLabel(label);
    } catch (err) {
      setLocationError(err instanceof GeolocationError ? err.message : describeError(err));
    } finally {
      setIsDetectingLocation(false);
    }
  }

  function handleDetailsSubmit(e: FormEvent) {
    e.preventDefault();
    setDetailsSaved(false);
    detailsMutation.mutate({
      phone,
      addressLine1,
      addressLine2,
      city,
      region,
      postalCode,
      country,
      locationLabel,
    });
  }

  if (!isInitialized || isLoadingPrincipal) return <LoadingState label="Loading profile…" />;
  if (!isSignedIn) {
    return (
      <div className="empty-state">
        <h1>Your profile</h1>
        <p>
          <Link to="/sign-in">Sign in</Link> to manage your profile.
        </p>
      </div>
    );
  }

  const currentUsername = principal?.username ?? null;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    setValidationError(null);
    // Validate with the same schema the API enforces, so the user gets the specific rule they
    // broke inline instead of a round-trip and a generic 400.
    const parsed = usernameSchema.safeParse(usernameInput);
    if (!parsed.success) {
      setValidationError(`Username ${parsed.error.issues[0]?.message ?? "is invalid"}.`);
      return;
    }
    mutation.mutate(parsed.data);
  }

  const conflictError =
    mutation.error instanceof ApiClientError && mutation.error.code === "USERNAME_TAKEN"
      ? "That username is already taken — try another."
      : null;

  return (
    <div className="stack" style={{ maxWidth: 560 }}>
      <h1>Your profile</h1>

      <div className="card stack">
        <div className="row" style={{ alignItems: "center", gap: "var(--space-3)" }}>
          {details && (
            <img
              src={details.avatarUrl}
              alt=""
              width={64}
              height={64}
              style={{ borderRadius: "50%" }}
            />
          )}
          <div>
            <p className="text-sm muted">Profile picture</p>
            <p className="text-sm muted">
              From <a href="https://gravatar.com" target="_blank" rel="noreferrer">Gravatar</a>,
              based on your account email. Only ever shown to you here — never on your listings
              or orders. Set one up at gravatar.com to change it.
            </p>
          </div>
        </div>

        <div>
          <p className="text-sm muted">Email</p>
          <p>{email ?? "—"}</p>
          <p className="text-sm muted">
            Your email comes from your Coinbase sign-in and is never shown to other people on
            MarketOnBase. It can&apos;t be changed here.
          </p>
        </div>

        <div>
          <p className="text-sm muted">Display name</p>
          {currentUsername ? (
            <p className="money">{currentUsername}</p>
          ) : (
            <p className="muted">You haven&apos;t set one yet.</p>
          )}
          <p className="text-sm muted">
            This is the only name buyers and sellers see on your listings and orders. Pick
            something you&apos;re willing to be known by publicly — it can be changed later, but
            past orders keep the name that was in effect when they were placed.
          </p>
        </div>

        <form className="stack" onSubmit={handleSubmit}>
          <div className="field">
            <label htmlFor="username">
              {currentUsername ? "Change display name" : "Choose a display name"}
            </label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              placeholder="e.g. pixel-forge"
              value={usernameInput}
              onChange={(e) => {
                setUsernameInput(e.target.value);
                setSaved(false);
              }}
            />
            <p className="text-sm muted">
              3–30 characters: letters, numbers, and single hyphens or underscores in the middle.
            </p>
          </div>

          {validationError && (
            <p className="field-error-text" role="alert">
              {validationError}
            </p>
          )}
          {conflictError && (
            <p className="field-error-text" role="alert">
              {conflictError}
            </p>
          )}
          {mutation.isError && !conflictError && (
            <p className="field-error-text" role="alert">
              {describeError(mutation.error)}
            </p>
          )}
          {saved && (
            <p className="alert alert--success" role="status">
              Display name saved.
            </p>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={mutation.isPending || usernameInput.trim().length === 0}
          >
            {mutation.isPending ? "Saving…" : "Save display name"}
          </button>
        </form>
      </div>

      <div className="card stack">
        <h2>Contact &amp; location</h2>
        <p className="text-sm muted">
          Only visible to you — never shown to other buyers or sellers.
        </p>

        {detailsLoading ? (
          <LoadingState label="Loading…" />
        ) : (
          <form className="stack" onSubmit={handleDetailsSubmit}>
            <div className="field">
              <label htmlFor="phone">Phone number</label>
              <input
                id="phone"
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="location-label">Location</label>
              <div className="row">
                <input
                  id="location-label"
                  type="text"
                  placeholder="e.g. Austin, TX"
                  autoComplete="address-level2"
                  value={locationLabel}
                  onChange={(e) => setLocationLabel(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={isDetectingLocation}
                  onClick={() => void handleUseLocation()}
                >
                  {isDetectingLocation ? "Detecting…" : "Use my current location"}
                </button>
              </div>
              {locationError && (
                <p className="field-error-text" role="alert">
                  {locationError}
                </p>
              )}
            </div>

            <div className="field">
              <label htmlFor="address1">Address line 1</label>
              <input
                id="address1"
                type="text"
                autoComplete="address-line1"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="address2">Address line 2</label>
              <input
                id="address2"
                type="text"
                autoComplete="address-line2"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
              />
            </div>

            <div className="row">
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="city">City</label>
                <input
                  id="city"
                  type="text"
                  autoComplete="address-level2"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="region">State / region</label>
                <input
                  id="region"
                  type="text"
                  autoComplete="address-level1"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                />
              </div>
            </div>

            <div className="row">
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="postal-code">Postal code</label>
                <input
                  id="postal-code"
                  type="text"
                  autoComplete="postal-code"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="country">Country</label>
                <input
                  id="country"
                  type="text"
                  autoComplete="country-name"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </div>
            </div>

            {detailsMutation.isError && (
              <p className="field-error-text" role="alert">
                {describeError(detailsMutation.error)}
              </p>
            )}
            {detailsSaved && (
              <p className="alert alert--success" role="status">
                Saved.
              </p>
            )}

            <button type="submit" className="btn btn-primary" disabled={detailsMutation.isPending}>
              {detailsMutation.isPending ? "Saving…" : "Save contact & location"}
            </button>
          </form>
        )}
      </div>

      <AccountSettings />

      <div className="card stack">
        <h2>Account</h2>
        <p className="text-sm muted">
          Your wallet, balances, and payouts live on the <Link to="/wallet">wallet page</Link>.
        </p>
      </div>
    </div>
  );
}
